import React, { useState, useEffect } from 'react';
import { Calendar, Home, Clock, Backpack } from 'lucide-react';
import { format } from 'date-fns';
import { apiClient } from '../../utils/api';
import { useFeature } from '../../contexts/FeaturesContext';
import { useCustodyParents, custodySideOf, custodyLabel } from '../../hooks/useCustodyParents';
import { getTodayDateString } from '../../utils/timezoneUtils';

interface CustodyDay {
  date: string;
  // "With <parent>" from the family's custody settings
  location: string;
  isHoliday: boolean;
  holidayName?: string;
  isSchoolBreak?: boolean;
  schoolEventDesc?: string;
}

interface WhereAmIProps {
  // The kid's username; the custody endpoint is keyed on it.
  username: string;
}

// Days shown in the widget, starting today.
const DAYS_SHOWN = 14;

/**
 * GET /api/custody/schedule/:username returns
 * `{ schedule: [{ date, child, location, is_holiday, is_school_day }] }`
 * covering the past 4 weeks through the next 12, ignoring any date params.
 * Accepts a bare array too, and keeps only today onward.
 */
function parseCustodySchedule(response: unknown, todayStr: string): CustodyDay[] {
  const rows: unknown[] = Array.isArray(response)
    ? response
    : Array.isArray((response as { schedule?: unknown })?.schedule)
      ? (response as { schedule: unknown[] }).schedule
      : [];

  return rows
    .filter((row): row is Record<string, unknown> =>
      !!row && typeof row === 'object' &&
      typeof (row as Record<string, unknown>).date === 'string' &&
      typeof (row as Record<string, unknown>).location === 'string')
    .map(row => ({
      date: String(row.date).slice(0, 10),
      location: String(row.location),
      isHoliday: row.is_holiday === true || row.isHoliday === true,
      holidayName: typeof row.holiday_name === 'string' ? row.holiday_name : undefined,
    }))
    .filter(day => !todayStr || day.date >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, DAYS_SHOWN);
}

// Whole days between two YYYY-MM-DD strings, independent of time of day.
function daysBetween(fromDate: string, toDate: string): number {
  const toUtc = (d: string) => {
    const [y, m, day] = d.split('-').map(Number);
    return Date.UTC(y, m - 1, day);
  };
  return Math.round((toUtc(toDate) - toUtc(fromDate)) / 86400000);
}

export default function WhereAmI({ username }: WhereAmIProps) {
  const custodyEnabled = useFeature('custody_schedule');
  const custodyParents = useCustodyParents();
  const [schedule, setSchedule] = useState<CustodyDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [nextTransitionDate, setNextTransitionDate] = useState<string | null>(null);

  // The feature check is made inside the effect rather than by an early return
  // above it. useFeature resolves asynchronously, so custodyEnabled flips from
  // falsy to true on a later render; returning before the hook meant the first
  // render registered no hooks and the next registered one, which is the
  // "Rendered more hooks than during the previous render" crash.
  useEffect(() => {
    if (!custodyEnabled || !username) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadSchedule = async () => {
      setLoading(true);
      try {
        // Family timezone, not the browser's, decides what "today" is.
        const todayStr = await getTodayDateString();
        const response = await apiClient.get(`/api/custody/schedule/${encodeURIComponent(username.toLowerCase())}`);
        if (cancelled) return;

        const days = parseCustodySchedule(response, todayStr);
        setSchedule(days);

        const current = days[0]?.location;
        const transition = days.find(day => day.location !== current);
        setNextTransitionDate(transition ? transition.date : null);
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load custody schedule:', error);
          setSchedule([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSchedule();
    return () => {
      cancelled = true;
    };
  }, [username, custodyEnabled]);

  // Hide component if custody feature is disabled. Must stay below every hook.
  if (!custodyEnabled || !username) {
    return null;
  }

  const nextTransition = nextTransitionDate ? new Date(nextTransitionDate + 'T12:00:00') : null;

  const getDaysUntilTransition = () => {
    if (!nextTransitionDate || schedule.length === 0) return null;
    return daysBetween(schedule[0].date, nextTransitionDate);
  };

  const shouldShowPackingReminder = () => {
    const days = getDaysUntilTransition();
    return days !== null && days === 1;
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-lg p-4 h-48"></div>
    );
  }

  const today = schedule && schedule.length > 0 ? schedule[0] : null;
  const daysUntil = getDaysUntilTransition();

  // If no schedule data, don't render anything
  if (!today || schedule.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Home className="w-5 h-5 text-blue-500" />
          Where Am I?
        </h3>
        {shouldShowPackingReminder() && (
          <div className="flex items-center gap-1 text-orange-500 text-sm animate-pulse">
            <Backpack className="w-4 h-4" />
            <span>Pack tonight!</span>
          </div>
        )}
      </div>

      {/* Current Location */}
      <div className={`p-4 rounded-lg mb-4 ${
        custodySideOf(today?.location, custodyParents) === 'primary'
          ? 'bg-green-50 border-2 border-green-300'
          : 'bg-blue-50 border-2 border-blue-300'
      }`}>
        <div className="text-sm text-gray-600 mb-1">Today</div>
        <div className="text-xl font-bold">
          {today?.location || 'Unknown'}
        </div>
        {today?.isHoliday && (
          <div className="text-sm text-purple-600 mt-1">
            🎉 {today.holidayName}
          </div>
        )}
        {today?.isSchoolBreak && (
          <div className="text-sm text-orange-600 mt-1">
            🏖️ {today.schoolEventDesc || 'School Break'}
          </div>
        )}
      </div>

      {/* Next Transition */}
      {nextTransition && (
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Next transition:</span>
            </div>
            <div className="text-sm font-semibold">
              {daysUntil === 0 ? 'Today!' :
               daysUntil === 1 ? 'Tomorrow' :
               `In ${daysUntil} days`}
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {format(nextTransition, 'EEEE, MMMM d')}
          </div>
        </div>
      )}

      {/* Next 7 Days Preview */}
      <div className="mt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Next Week</h4>
        <div className="grid grid-cols-7 gap-1">
          {schedule.slice(0, 7).map((day, index) => (
            <div
              key={day.date}
              className={`text-center p-2 rounded text-xs ${
                custodySideOf(day.location, custodyParents) === 'primary'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-blue-100 text-blue-800'
              } ${index === 0 ? 'ring-2 ring-gray-400' : ''}`}
              title={`${format(new Date(day.date + 'T12:00:00'), 'MMM d')} - ${day.location}`}
            >
              <div className="font-semibold">
                {format(new Date(day.date + 'T12:00:00'), 'EEE')}
              </div>
              <div className="text-xs">
                {format(new Date(day.date + 'T12:00:00'), 'd')}
              </div>
              {day.isHoliday && <div className="text-xs">🎉</div>}
              {day.isSchoolBreak && <div className="text-xs">🏖️</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-100 rounded border border-green-300"></div>
          <span>{custodyLabel(custodyParents, 'primary')}</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-100 rounded border border-blue-300"></div>
          <span>{custodyLabel(custodyParents, 'other')}</span>
        </div>
      </div>
    </div>
  );
}